import {
    EditorOperations,
    SagaEditor,
    isTable,
    isTableRow,
    TableRow,
    SpaceOperations,
    TaskBlock,
    Mention,
    InlinePageLink,
    InlineCollection,
    GoogleDriveLink,
    LinearIssue,
    DateBlock,
    SafeMapOutput,
    TABLE_CONSTANTS,
} from '@saga/shared';
import classNames from 'classnames';
import React from 'react';
import { Node, NodeEntry, Element, Transforms, Editor } from 'slate';
import { ReactEditor } from 'slate-react';
import { MoreVertical, Plus } from 'react-feather';
import * as Popover from '@radix-ui/react-popover';
import Button from '@/components/styled/Button';
import { useTranslation } from 'react-i18next';
import { SpaceContext } from '@/components/SpaceProvider';
import { TableRowMenu } from './TableRowMenu';
import { tableRowPlugin } from './TableRow';
import { tableCellPlugin } from './TableCell';
import { TableDimensionsContext, TableSelectionContext, TableHoverContext } from './TableContexts';

// Re-export contexts for backward compatibility
export { TableDimensionsContext, TableSelectionContext, TableHoverContext } from './TableContexts';

// Backward compatibility: Export useTableContext for existing code
// This returns the dimensions context which is sufficient for checking if we're in a table
export const useTableContext = () => React.useContext(TableDimensionsContext);

// Custom hook that creates a text resolver with live data when available
const useLiveDataResolver = (space: SafeMapOutput<any> | undefined): EditorOperations.Tables.CellTextResolver => {
    return React.useMemo((): EditorOperations.Tables.CellTextResolver => {
        // Using a registry pattern for maintainability
        const blockResolvers = new Map<string, (element: any, space?: SafeMapOutput<any>) => string | undefined>([
            [
                'task-block',
                (el: TaskBlock, space?: SafeMapOutput<any>) => {
                    if (space) {
                        try {
                            const liveTask = SpaceOperations.findTask(space, el.taskId);
                            if (liveTask?.title) return liveTask.title;
                        } catch (e) {
                            // Task not found, fall back below
                        }
                    }
                    return el.staticTask?.title || `Task: ${el.taskId}`;
                },
            ],
        ]);

        const inlineResolvers = new Map<string, (element: any, space?: SafeMapOutput<any>) => string | undefined>([
            [
                'mention',
                (el: Mention) => {
                    return el.creatorName || `@${el.memberId}`;
                },
            ],
            [
                'inline-page-link',
                (el: InlinePageLink, space?: SafeMapOutput<any>) => {
                    if (space) {
                        try {
                            const livePage = SpaceOperations.getPageById(space, el.pageId, ['title']);
                            if (livePage?.title) return livePage.title;
                        } catch (e) {
                            // Page not found, fall back below
                        }
                    }
                    return el.staticPage?.title || `Page: ${el.pageId}`;
                },
            ],
            [
                'inline-collection',
                (el: InlineCollection, space?: SafeMapOutput<any>) => {
                    if (space) {
                        try {
                            const collectionMap = SpaceOperations.findCollectionById(space, el.collectionId);
                            const title = collectionMap?.get('title');
                            if (title && typeof title === 'string') return title;
                        } catch (e) {
                            // Collection not found, fall back below
                        }
                    }
                    return el.staticCollection?.title || `Collection: ${el.collectionId}`;
                },
            ],
            [
                'google-drive-link',
                (el: GoogleDriveLink) => {
                    if (el.state.type === 'loaded' && el.state.file.name) {
                        return el.state.file.name;
                    }
                    return 'Google Drive File';
                },
            ],
            [
                'linear-issue',
                (el: LinearIssue) => {
                    if (el.state.type === 'loaded' && el.state.issue.title) {
                        return el.state.issue.title;
                    }
                    return 'Linear Issue';
                },
            ],
            [
                'date-block',
                (el: DateBlock) => {
                    // Return the ISO date string directly for proper date sorting
                    // The date is stored in ISO format (YYYY-MM-DD)
                    return el.date || '';
                },
            ],
        ]);

        return {
            getCellSortText(cell: Node): string {
                // For performance, we only inspect the first block element and its first two children
                // to generate a sort key. This is a trade-off for speed in tables with rich content.
                if (!Element.isElement(cell) || !cell.children.length) {
                    return '';
                }

                const firstBlock = cell.children[0];

                // Handle block-level elements
                if (Element.isElement(firstBlock)) {
                    const blockResolver = blockResolvers.get(firstBlock.type);
                    if (blockResolver) {
                        return blockResolver(firstBlock, space) || '';
                    }
                }

                if (!Element.isElement(firstBlock)) {
                    return Node.string(firstBlock).trim();
                }

                // For paragraph-like blocks, resolve their inline children
                return firstBlock.children
                    .slice(0, TABLE_CONSTANTS.MAX_CHILDREN_FOR_SORT_TEXT)
                    .map((child: Node) => {
                        if (Element.isElement(child)) {
                            const resolver = inlineResolvers.get(child.type);
                            if (resolver) {
                                return resolver(child, space) || Node.string(child);
                            }
                        }
                        return Node.string(child);
                    })
                    .join('')
                    .trim();
            },
        };
    }, [space]);
};

function getNodeEntry<T extends Node>(editor: ReactEditor, element: T): NodeEntry<T> {
    const path = ReactEditor.findPath(editor, element);
    return [element, path];
}

export type TableFeatures = {
    appendRow(): void;
    appendColumn(): void;
    deleteColumnByIndex(index: number): void;
    deleteRowByIndex(index: number): void;
    insertRowAbove(index: number): void;
    insertRowBelow(index: number): void;
    moveColumnLeft(index: number): void;
    moveColumnRight(index: number): void;
    moveRowUp(index: number): void;
    moveRowDown(index: number): void;
    sortByColumn(index: number, direction: 'asc' | 'desc'): void;
    onAfterMenuOpen(): void;
    duplicateRow(index: number): void;
    duplicateColumn(index: number): void;
};

function AppendButton({
    onMouseDown,
    isVisble,
    name,
    className,
    style,
}: {
    onMouseDown: React.MouseEventHandler;
    isVisble?: boolean;
    name: string;
    className?: string;
    style?: React.CSSProperties;
}) {
    return (
        <button
            onMouseDown={onMouseDown}
            contentEditable={false}
            className={classNames(
                'flex justify-center items-center dark:bg-zinc-600 bg-saga-gray-200 hover:opacity-100 transition-opacity cursor-pointer z-10 pointer-events-auto select-none absolute rounded',
                className,
                {
                    'opacity-40': isVisble,
                    'opacity-0 pointer-events-none sm:pointer-events-auto': !isVisble,
                },
            )}
            style={style}
        >
            <Plus size={12} />
            <span className="sr-only">{name}</span>
        </button>
    );
}

const tablePlugin = SagaEditor.Plugins.createBlockPlugin({
    match: isTable,
    normalizers: [SagaEditor.Normalizers.tableNormalizer],
    Component({ element, children, selected, editor }) {
        const { canEdit } = SagaEditor.useEditorContext();
        const [menuOpenForColumn, setMenuOpenForColumn] = React.useState<number | null>(null);
        const [menuOpenForRow, setMenuOpenForRow] = React.useState<number | null>(null);
        const [selectedRowIndex, setSelectedRowIndex] = React.useState<number | null>(null);
        const [isHoveringLastColumn, setIsHoveringLastColumn] = React.useState(false);
        const [isHoveringLastRow, setIsHoveringLastRow] = React.useState(false);
        const [hoveringResizeColumn, setHoveringResizeColumn] = React.useState<number | null>(null);
        const [resizePreview, setResizePreview] = React.useState<{ column: number; width: number } | null>(null);
        const tableRef = React.useRef<HTMLTableElement>(null);
        const scrollContainerRef = React.useRef<HTMLDivElement>(null);
        const { t } = useTranslation();

        // Initialize column widths if not present
        const columnWidths = React.useMemo(() => {
            const [columns] = element.dimensions;

            if (!element.columnWidths || element.columnWidths.length !== columns) {
                // Initialize with default widths
                return Array(columns).fill(TABLE_CONSTANTS.DEFAULT_COLUMN_WIDTH);
            }
            return element.columnWidths;
        }, [element.dimensions, element.columnWidths]);

        // Get space context for live data access
        // We use useContext directly to avoid conditional hook usage
        // This might be null in contexts like public pages
        const spaceContext = React.useContext(SpaceContext);
        const space = spaceContext?.space;

        // Use custom hook for live data resolution
        const liveDataResolver = useLiveDataResolver(space);

        // Callback for rows to report their selection status
        const handleRowSelectionChange = React.useCallback(
            (rowIndex: number, isSelected: boolean) => {
                if (isSelected) {
                    setSelectedRowIndex(rowIndex);
                } else if (selectedRowIndex === rowIndex) {
                    setSelectedRowIndex(null);
                }
            },
            [selectedRowIndex],
        );

        // Clear selection when table is no longer selected
        React.useEffect(() => {
            if (!selected) {
                setSelectedRowIndex(null);
            }
        }, [selected]);

        // Handle resize preview
        const handlePreviewChange = React.useCallback((columnIndex: number | null, width: number | null) => {
            if (columnIndex !== null && width !== null) {
                setResizePreview({ column: columnIndex, width });
            } else {
                setResizePreview(null);
            }
        }, []);

        // Handle column resize
        const handleColumnResize = React.useCallback(
            (columnIndex: number, newWidth: number) => {
                const newColumnWidths = [...columnWidths];
                newColumnWidths[columnIndex] = newWidth;

                // Update the table node with new column widths
                const nodeEntry = getNodeEntry(editor, element);
                Transforms.setNodes(editor, { columnWidths: newColumnWidths }, { at: nodeEntry[1] });
            },
            [editor, element, columnWidths],
        );

        const features = React.useMemo((): TableFeatures | undefined => {
            if (!canEdit) return undefined;

            const nodeEntry = getNodeEntry(editor, element);
            const [table] = nodeEntry;

            return {
                onAfterMenuOpen() {
                    ReactEditor.deselect(editor);
                },
                deleteColumnByIndex(index) {
                    ReactEditor.focus(editor);
                    EditorOperations.Tables.deleteColumnByIndex(editor, nodeEntry, index);
                },
                deleteRowByIndex(index) {
                    ReactEditor.focus(editor);
                    const [table, tablePath] = nodeEntry;

                    // Get row directly from the table's children
                    if (table.children && index < table.children.length) {
                        const rowNode = table.children[index];
                        if (isTableRow(rowNode)) {
                            const rowPath = [...tablePath, index];
                            EditorOperations.Tables.deleteRow(editor, [rowNode, rowPath] as NodeEntry<TableRow>);
                        }
                    }
                },
                insertRowAbove(index) {
                    ReactEditor.focus(editor);
                    EditorOperations.Tables.insertRow(editor, nodeEntry, index);
                },
                insertRowBelow(index) {
                    ReactEditor.focus(editor);
                    EditorOperations.Tables.insertRow(editor, nodeEntry, index + 1);
                },
                appendColumn() {
                    EditorOperations.Tables.appendColumn(editor, nodeEntry);
                },
                appendRow() {
                    EditorOperations.Tables.appendRow(editor, nodeEntry);
                },
                moveColumnLeft(index) {
                    if (index > 0) {
                        ReactEditor.focus(editor);
                        EditorOperations.Tables.moveColumn(editor, nodeEntry, index, index - 1);
                    }
                },
                moveColumnRight(index) {
                    if (index < table.dimensions[0] - 1) {
                        ReactEditor.focus(editor);
                        EditorOperations.Tables.moveColumn(editor, nodeEntry, index, index + 1);
                    }
                },
                moveRowUp(index) {
                    if (index > 0) {
                        ReactEditor.focus(editor);
                        EditorOperations.Tables.moveRow(editor, nodeEntry, index, index - 1);
                    }
                },
                moveRowDown(index) {
                    const [table] = nodeEntry;
                    if (index < table.children.length - 1) {
                        ReactEditor.focus(editor);
                        EditorOperations.Tables.moveRow(editor, nodeEntry, index, index + 1);
                    }
                },
                sortByColumn(index, direction) {
                    ReactEditor.focus(editor);
                    // Use live data resolver when space is available
                    EditorOperations.Tables.sortTableByColumn(
                        editor,
                        nodeEntry,
                        index,
                        direction,
                        space ? liveDataResolver : undefined,
                    );
                },
                duplicateRow(index) {
                    ReactEditor.focus(editor);
                    EditorOperations.Tables.duplicateRow(editor, nodeEntry, index);
                },
                duplicateColumn(index) {
                    ReactEditor.focus(editor);
                    EditorOperations.Tables.duplicateColumn(editor, nodeEntry, index);
                },
            };
        }, [editor, element, canEdit, space, liveDataResolver]);

        // Stable context for dimensions and features - rarely changes
        const dimensionsContext = React.useMemo(
            () => ({
                totalColumns: element.dimensions[0],
                totalRows: element.dimensions[1],
                columnWidths,
                features,
                onColumnResize: handleColumnResize,
                onPreviewChange: handlePreviewChange,
            }),
            [element.dimensions, columnWidths, features, handleColumnResize, handlePreviewChange],
        );

        // Selection context - changes with user interaction
        const selectionContext = React.useMemo(
            () => ({
                menuOpenForColumn,
                menuOpenForRow,
                setMenuOpenForColumn,
                setMenuOpenForRow,
                selectedRowIndex,
                setSelectedRowIndex,
                onRowSelectionChange: handleRowSelectionChange,
            }),
            [menuOpenForColumn, menuOpenForRow, selectedRowIndex, handleRowSelectionChange],
        );

        // Hover context - only for resize handles
        const hoverContext = React.useMemo(
            () => ({
                hoveringResizeColumn,
                setHoveringResizeColumn,
            }),
            [hoveringResizeColumn],
        );

        // Track row elements more efficiently using a ref
        const rowsRef = React.useRef<HTMLTableRowElement[]>([]);
        const updateRows = React.useCallback(() => {
            if (!tableRef.current) {
                rowsRef.current = [];
                return;
            }
            rowsRef.current = Array.from(tableRef.current.querySelectorAll('tr'));
        }, []);

        // Update rows when table structure changes
        React.useLayoutEffect(() => {
            updateRows();
        }, [children, updateRows]);

        // Clean up rowsRef on unmount to prevent memory leaks
        React.useEffect(() => {
            return () => {
                rowsRef.current = [];
            };
        }, []);

        // Use a stable reference for rows in render
        const rows = rowsRef.current;

        const totalRows = React.useMemo(() => {
            const [tableNode] = getNodeEntry(editor, element);
            return tableNode.children.length;
        }, [editor, element]);

        // Handle table hover to detect last row/column
        const handleTableMouseMove = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
            if (!tableRef.current) return;

            const tableRect = tableRef.current.getBoundingClientRect();
            const relativeX = e.clientX - tableRect.left;
            const relativeY = e.clientY - tableRect.top;

            // Check if hovering over last column area (within 100px of right edge)
            const isNearLastColumn = relativeX > tableRect.width - 100;
            setIsHoveringLastColumn(isNearLastColumn);

            // Check if hovering over last row area (within 50px of bottom edge)
            const isNearLastRow = relativeY > tableRect.height - 50;
            setIsHoveringLastRow(isNearLastRow);
        }, []);

        const handleTableMouseLeave = React.useCallback(() => {
            setIsHoveringLastColumn(false);
            setIsHoveringLastRow(false);
        }, []);

        return (
            <TableDimensionsContext.Provider value={dimensionsContext}>
                <TableSelectionContext.Provider value={selectionContext}>
                    <TableHoverContext.Provider value={hoverContext}>
                        <div className={classNames('w-full relative')}>
                            <div
                                data-testid="table"
                                className="pt-3 relative"
                                style={{
                                    maxWidth: '100%',
                                }}
                            >
                                <div className="relative">
                                    {/* Row menu buttons positioned absolutely outside scrollable area */}
                                    {selectedRowIndex !== null && features && rows.length > selectedRowIndex && (
                                        <Popover.Root
                                            onOpenChange={(open) => {
                                                if (open) {
                                                    setMenuOpenForRow(selectedRowIndex);
                                                } else {
                                                    setMenuOpenForRow(null);
                                                }
                                            }}
                                            open={menuOpenForRow === selectedRowIndex}
                                        >
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    left: -18,
                                                    top:
                                                        12 + // pt-3 offset (3 * 4px = 12px)
                                                        rows[selectedRowIndex].offsetTop +
                                                        rows[selectedRowIndex].offsetHeight / 2,
                                                    transform: 'translateY(-50%)',
                                                    zIndex: 999,
                                                    pointerEvents: 'auto',
                                                }}
                                            >
                                                <Popover.Trigger asChild>
                                                    <div
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                        className="opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
                                                    >
                                                        <Button.Plain>
                                                            <div className="shadow-md bg-white dark:bg-zinc-600 rounded border dark:border-zinc-600 border-saga-gray-200">
                                                                <MoreVertical size={16} />
                                                                <span className="sr-only">
                                                                    {t('common.open_context_menu_for_row', {
                                                                        row: selectedRowIndex + 1,
                                                                    })}
                                                                </span>
                                                            </div>
                                                        </Button.Plain>
                                                    </div>
                                                </Popover.Trigger>
                                                <TableRowMenu
                                                    rowIndex={selectedRowIndex}
                                                    totalRows={totalRows}
                                                    onMoveUp={() => {
                                                        features.moveRowUp(selectedRowIndex);
                                                        setMenuOpenForRow(null);
                                                        // Adjust selectedRowIndex after moving up
                                                        setSelectedRowIndex(Math.max(0, selectedRowIndex - 1));
                                                    }}
                                                    onMoveDown={() => {
                                                        features.moveRowDown(selectedRowIndex);
                                                        setMenuOpenForRow(null);
                                                        // Adjust selectedRowIndex after moving down
                                                        setSelectedRowIndex(
                                                            Math.min(totalRows - 1, selectedRowIndex + 1),
                                                        );
                                                    }}
                                                    onDelete={() => {
                                                        features.deleteRowByIndex(selectedRowIndex);
                                                        setMenuOpenForRow(null);
                                                    }}
                                                    onInsertAbove={() => {
                                                        features.insertRowAbove(selectedRowIndex);
                                                        setMenuOpenForRow(null);
                                                    }}
                                                    onInsertBelow={() => {
                                                        features.insertRowBelow(selectedRowIndex);
                                                        setMenuOpenForRow(null);
                                                        // Adjust selectedRowIndex after inserting below
                                                        setSelectedRowIndex(selectedRowIndex + 1);
                                                    }}
                                                    onSetBackgroundColor={(color: string) => {
                                                        // Apply color to all cells in the row
                                                        const [tableNode, tablePath] = getNodeEntry(editor, element);
                                                        const rowPath = [...tablePath, selectedRowIndex];
                                                        const row = tableNode.children[selectedRowIndex];

                                                        Editor.withoutNormalizing(editor, () => {
                                                            row.children.forEach((_: any, cellIndex: number) => {
                                                                const cellPath = [...rowPath, cellIndex];
                                                                Transforms.setNodes(
                                                                    editor,
                                                                    {
                                                                        backgroundColor:
                                                                            color === 'blank' ? undefined : color,
                                                                    },
                                                                    { at: cellPath },
                                                                );
                                                            });
                                                        });

                                                        setMenuOpenForRow(null);
                                                    }}
                                                    onDuplicate={() => {
                                                        ReactEditor.focus(editor);
                                                        features.duplicateRow(selectedRowIndex);
                                                        setMenuOpenForRow(null);
                                                        // Select the newly duplicated row
                                                        setSelectedRowIndex(selectedRowIndex + 1);
                                                    }}
                                                />
                                            </div>
                                        </Popover.Root>
                                    )}
                                    <div
                                        ref={scrollContainerRef}
                                        className="overflow-x-auto overflow-y-hidden scroll-on-hover-y pt-3 -mt-3 pb-5"
                                    >
                                        <div
                                            className="relative inline-block"
                                            onMouseMove={handleTableMouseMove}
                                            onMouseLeave={handleTableMouseLeave}
                                        >
                                            <div className="rounded border dark:border-zinc-600">
                                                <table
                                                    ref={tableRef}
                                                    className="border-collapse"
                                                    id={element.id}
                                                    style={{
                                                        tableLayout: 'fixed',
                                                        width: columnWidths.reduce((sum, width) => sum + width, 0),
                                                    }}
                                                >
                                                    <tbody className="divide-y dark:divide-zinc-600" ref={updateRows}>
                                                        {children}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {/* Column resize indicators */}
                                            {hoveringResizeColumn !== null && (
                                                <div
                                                    className="absolute top-0 bottom-0 w-1 bg-blue-500 opacity-50 pointer-events-none z-50"
                                                    style={{
                                                        left:
                                                            columnWidths
                                                                .slice(0, hoveringResizeColumn + 1)
                                                                .reduce((sum, w) => sum + w, 0) - 2,
                                                    }}
                                                />
                                            )}
                                            {/* Resize preview indicator */}
                                            {resizePreview && (
                                                <div
                                                    className="absolute top-0 bottom-0 w-1 bg-blue-500 opacity-50 pointer-events-none z-50"
                                                    style={{
                                                        left:
                                                            columnWidths
                                                                .slice(0, resizePreview.column)
                                                                .reduce((sum, w) => sum + w, 0) +
                                                            resizePreview.width -
                                                            2,
                                                    }}
                                                />
                                            )}
                                            {features && (
                                                <>
                                                    <AppendButton
                                                        name={t('table.append_column')}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            features.appendColumn();
                                                        }}
                                                        isVisble={selected || isHoveringLastColumn}
                                                        className="top-0 bottom-0 w-4"
                                                        style={{
                                                            left: '100%',
                                                            marginLeft: 2,
                                                        }}
                                                    />
                                                    <AppendButton
                                                        name={t('table.append_row')}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            features.appendRow();
                                                        }}
                                                        isVisble={selected || isHoveringLastRow}
                                                        className="left-0 right-0 h-4"
                                                        style={{
                                                            top: '100%',
                                                            marginTop: 2,
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TableHoverContext.Provider>
                </TableSelectionContext.Provider>
            </TableDimensionsContext.Provider>
        );
    },
});

export const tablePlugins = SagaEditor.Plugins.combine(tablePlugin, tableRowPlugin, tableCellPlugin);
